import { useMemo } from 'react';
import { Link } from 'react-router';
import type { Role } from '@gitrader/shared';
import { Icon } from '../components/Icon';
import { firstLeaf, flatten, visibleFor } from '../routes/helpers';
import { ROUTE_TREE } from '../routes/tree';
import type { RouteNode } from '../routes/types';
import { LIVE_TAB_ACTIVE } from './live/liveStyles';

/** ≤900px: the top-level sections as a bottom tab bar. Tapping one opens its first child. */
export function MobileTabBar({
  role,
  activeChain,
}: {
  role: Role;
  activeChain: readonly RouteNode[];
}) {
  const tops = useMemo(() => visibleFor(ROUTE_TREE, role), [role]);
  const byId = useMemo(() => new Map(flatten(ROUTE_TREE).map((r) => [r.node.id, r])), []);
  const activeIds = new Set(activeChain.map((n) => n.id));
  const liveActive = activeChain.some((n) => n.env === 'live');

  return (
    <nav
      aria-label="Sections"
      data-tabbar
      className="fixed inset-x-0 bottom-0 z-[25] grid border-t border-border bg-surface pb-[env(safe-area-inset-bottom)] desk:hidden"
      style={{ gridTemplateColumns: `repeat(${tops.length}, minmax(0, 1fr))` }}
    >
      {tops.map((top) => {
        const resolved = byId.get(top.id);
        const leaf = resolved ? firstLeaf(resolved, role) : undefined;
        const active = activeIds.has(top.id);
        const activeClass =
          active && top.id === 'trade' && liveActive ? LIVE_TAB_ACTIVE : 'text-accent-ink';
        return (
          <Link
            key={top.id}
            to={leaf?.fullPath ?? '/'}
            data-tab-id={top.id}
            aria-current={active ? 'true' : undefined}
            className={`flex min-w-0 flex-col items-center gap-[3px] px-0.5 pt-2 pb-[7px] text-[10px] font-semibold ${active ? activeClass : 'text-ink-3'}`}
          >
            <Icon name={top.icon} size={17} />
            <span className="truncate">{top.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}
